import { Field, NoSQLCollection } from '@nexla/@types';
import UIUtils from 'js/lib/UIUtils';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import _, { assign, omit } from 'lodash';
import { getStartCronField } from 'new/components/flows/types/DirectFlow/utils';

type Params = {
  config: any;
  resource: any;
  prefix?: 'sourceConfig' | 'sinkConfig';
};

export const initializeFormToNoSQLCategory = (configurations: Params) => {
  const { resource, config, prefix = 'sourceConfig' } = configurations;

  // Older source saves omitted mode; restore the selector before editing filter fields.
  let mode = config?.mode;
  if (!mode && prefix === 'sourceConfig') {
    const hasQuery = [
      'mongo.query.expression',
      'dynamodb.filter.expression',
      'firebase.query',
    ].some((key) => !_.isEmpty(config?.[key]));
    if (hasQuery) {
      mode = 'query';
    } else if (!_.isEmpty(config?.['timestamp.key'])) {
      mode = 'timestamp';
    }
  }
  const resourceConfig = { ...config, mode: mode || 'default' };

  let data: any = {};
  if (resource?.id) {
    const collection = config?.collection;
    const id = `${config?.database}/${collection}`;

    data = {
      ...data,
      selectedCollection: { id, name: collection },
      selectedBucket: { id, name: collection, database: config?.database },
      [prefix]: {
        ...resourceConfig,
        sourceName: resource?.name,
        sourceDescription: resource?.description,
      },
    };

    return data;
  }

  data.selectedDatabase = null;
  data.selectedCollection = null;
  data[prefix] = resourceConfig;
  return data;
};

export const validateNoSQLSourceConfig = (config, allFields: Field[]) => {
  const fields = allFields.filter(
    (field: Field) =>
      getStartCronField(field) === false && UIUtils.shouldRenderField(field, config),
  );

  const missingFields = getEmptyRequiredFields(fields, config);

  if (missingFields.length) {
    const error = _.reduce(
      missingFields,
      (message, field) => `${message} ${field.display_name},`,
      'Please enter the following required fields: ',
    );

    return { error: true, message: error.substring(0, error.length - 1) };
  }

  if (config.db_query_mode === 'Default' && !config.collection) {
    return {
      error: true,
      message: 'Please select a collection to read data from.',
    };
  }

  return { error: false };
};

export const getNoSQLCollectionConfig = (
  collection: NoSQLCollection,
  sourceConfig: any,
  prefix = 'sourceConfig',
) => {
  let bucketObject = { collection: null, database: null };

  if (collection) {
    bucketObject = assign({}, bucketObject, omit(collection, ['scanned', 'columns', 'name', 'id']));

    bucketObject.collection = collection?.name ?? null;
  }

  const sourceConfigWithBucket = assign({}, sourceConfig, bucketObject);
  return {
    selectedCollection: collection,
    selectedBucket: collection,
    [prefix]: sourceConfigWithBucket,
  };
};
